import React, { useEffect, useState } from 'react';
import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import { useAppliance } from '@/lib/useAppliance';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toastError, toastSuccess } from '@/lib/toast';
import { DnsCacheConfigDTO } from '@webip/shared/src/dto/appliance';
import FormDnsCache from './FormDnsCache';

const defaultDnsConfig = (applianceId: string): Partial<DnsCacheConfigDTO> => ({
    appliance_id: Number(applianceId),
    ativo: false,
    servidores: ['1.1.1.1', '8.8.8.8'],
    entradas_estaticas: [],
    capturar_dns: true,
    restringir_doh: true,
    restringir_dot: true,
    restringir_quic: true,
});

export default function DnsCachePage() {
    const { applianceId, selectedAppliance } = useAppliance();
    const qc = useQueryClient();
    const [model, setModel] = useState<Partial<DnsCacheConfigDTO>>(
        applianceId ? defaultDnsConfig(applianceId) : {},
    );
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const query = useQuery(
        ['dns-config', applianceId],
        async () => {
            const res = await axios.get<{ ok: boolean; result: DnsCacheConfigDTO }>(
                `/api/appliance/${applianceId}/dns`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            enabled: !!applianceId,
            retry: false,
            onSuccess: (data) => setModel({ ...data }),
            onError: () => setModel(applianceId ? defaultDnsConfig(applianceId) : {}),
        },
    );

    useEffect(() => {
        setFieldErrors({});
        if (applianceId) setModel(defaultDnsConfig(applianceId));
    }, [applianceId]);

    const mutSave = useMutation({
        mutationFn: async (payload: Partial<DnsCacheConfigDTO>) => {
            return await axios.put(`/api/appliance/${applianceId}/dns`, payload, { withCredentials: true });
        },
        onSuccess: () => {
            toastSuccess('Configuração de cache DNS salva!');
            qc.invalidateQueries(['dns-config', applianceId]);
        },
        onError: (error: unknown) => {
            const msg = (error as any)?.response?.data?.message ?? 'Erro ao salvar';
            toastError(msg);
        },
    });

    const handleSave = () => {
        const errors: Record<string, string> = {};
        if (!Array.isArray(model.servidores) || model.servidores.length === 0) {
            errors.servidores = 'Informe ao menos um servidor DNS';
        }
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        mutSave.mutate(model);
    };

    return (
        <LayoutUsuario>
            <VBox style={{ gap: '16px', padding: '16px' }}>
                <PageHeader
                    title={`Cache DNS${selectedAppliance ? ` — ${selectedAppliance.nome}` : ''}`}
                    description="Configure o cache DNS para o appliance selecionado."
                    actions={applianceId ? (
                        <Botao
                            variant="primary"
                            onClick={handleSave}
                            disabled={mutSave.isLoading}
                        >
                            {mutSave.isLoading ? 'Salvando...' : 'Salvar'}
                        </Botao>
                    ) : undefined}
                />

                {!applianceId && (
                    <p style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>
                        Selecione um appliance no menu lateral.
                    </p>
                )}

                {applianceId && query.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '32px' }}>
                        <Icon path={mdiLoading} size={1.5} spin />
                    </HBox>
                )}

                {applianceId && !query.isLoading && (
                    <FormDnsCache
                        value={model}
                        onChange={setModel}
                        fieldErrors={fieldErrors}
                    />
                )}
            </VBox>
        </LayoutUsuario>
    );
}
